import { RRule, RRuleSet, datetime } from 'rrule';

/**
 * Recurrence utility functions using RRule (RFC 5545 standard)
 * This provides a reliable, battle-tested approach to recurring events
 * 
 * IMPORTANT: RRule operates in UTC by default. To handle local times correctly,
 * we use the `tzid` option or convert dates appropriately.
 */

/**
 * Create a UTC date from local date components (to avoid timezone shifts)
 * RRule expects dates in UTC, so we need to create a "fake UTC" date
 * that represents the local time we want
 */
function localDateToRRuleDate(date) {
    if (!date) return null;
    const d = new Date(date);
    // Create a UTC date with the same year/month/day/hour/minute as the local date
    return datetime(
        d.getFullYear(),
        d.getMonth() + 1, // datetime uses 1-indexed months
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
        d.getSeconds()
    );
}

/**
 * Convert RRule-generated UTC date back to a proper local Date
 * The RRule date has UTC values that actually represent local time
 */
function rruleDateToLocalDate(rruleDate) {
    if (!rruleDate) return null;
    // The rruleDate is in UTC but represents local time values
    // So we create a new local date with those values
    return new Date(
        rruleDate.getUTCFullYear(),
        rruleDate.getUTCMonth(),
        rruleDate.getUTCDate(),
        rruleDate.getUTCHours(),
        rruleDate.getUTCMinutes(),
        rruleDate.getUTCSeconds()
    );
}

// Weekday mapping for RRule
const WEEKDAY_MAP = {
    'Sunday': RRule.SU,
    'Monday': RRule.MO,
    'Tuesday': RRule.TU,
    'Wednesday': RRule.WE,
    'Thursday': RRule.TH,
    'Friday': RRule.FR,
    'Saturday': RRule.SA
};

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// RRule uses 0=Monday, so this array maps rrule weekday indices to labels
const RRULE_WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Frequency mapping
const FREQUENCY_MAP = {
    'Daily': RRule.DAILY,
    'Weekly': RRule.WEEKLY,
    'Monthly': RRule.MONTHLY,
    'Yearly': RRule.YEARLY
};

// Week position mapping for "nth weekday of month"
const WEEK_POSITION_MAP = {
    '1st': 1,
    '2nd': 2,
    '3rd': 3,
    '4th': 4,
    'Last': -1
};

/**
 * Common recurrence presets for quick selection
 * Note: These return RRule options, the caller should use localDateToRRuleDate for dtstart
 */
export const RECURRENCE_PRESETS = [
    { 
        id: 'daily', 
        label: 'Every Day',
        getRule: (startDate) => ({ 
            freq: RRule.DAILY, 
            interval: 1, 
            dtstart: localDateToRRuleDate(startDate) 
        })
    },
    { 
        id: 'weekly', 
        label: 'Every Week',
        getRule: (startDate) => ({ 
            freq: RRule.WEEKLY, 
            interval: 1, 
            byweekday: [WEEKDAY_MAP[WEEKDAY_LABELS[startDate.getDay()]]],
            dtstart: localDateToRRuleDate(startDate) 
        })
    },
    { 
        id: 'monthly', 
        label: 'Every Month (date)',
        getRule: (startDate) => ({ 
            freq: RRule.MONTHLY, 
            interval: 1, 
            bymonthday: [startDate.getDate()],
            dtstart: localDateToRRuleDate(startDate) 
        })
    },
    { 
        id: 'monthly_weekday', 
        label: 'Every Month (weekday)',
        getRule: (startDate) => {
            const weekOfMonth = Math.ceil(startDate.getDate() / 7);
            const weekday = WEEKDAY_MAP[WEEKDAY_LABELS[startDate.getDay()]];
            return { 
                freq: RRule.MONTHLY, 
                interval: 1, 
                byweekday: [weekday.nth(weekOfMonth)],
                dtstart: localDateToRRuleDate(startDate) 
            };
        }
    },
    { 
        id: 'yearly', 
        label: 'Every Year',
        getRule: (startDate) => ({ 
            freq: RRule.YEARLY, 
            interval: 1, 
            dtstart: localDateToRRuleDate(startDate) 
        })
    }
];

/**
 * Build an RRule from user-friendly recurrence settings
 * @param {Object} settings - Recurrence settings from UI
 * @param {Date} startDate - Event start date/time
 * @returns {RRule} - RRule instance
 */
export function buildRRule(settings, startDate) {
    // Convert local date to RRule-compatible UTC date
    const rruleStartDate = localDateToRRuleDate(startDate);
    
    const options = {
        dtstart: rruleStartDate,
        freq: FREQUENCY_MAP[settings.frequency] || RRule.WEEKLY,
        interval: parseInt(settings.interval) || 1
    };

    // Add end date if specified (convert to RRule date format)
    if (settings.endDate) {
        options.until = localDateToRRuleDate(settings.endDate);
    }

    // Handle frequency-specific options
    switch (settings.frequency) {
        case 'Weekly':
            if (settings.weekdays?.length > 0) {
                options.byweekday = settings.weekdays.map(day => WEEKDAY_MAP[day]);
            }
            break;

        case 'Monthly':
            if (settings.monthlyType === 'weekday' && settings.weekPosition && settings.weekday) {
                const position = WEEK_POSITION_MAP[settings.weekPosition];
                const weekday = WEEKDAY_MAP[settings.weekday];
                options.byweekday = [weekday.nth(position)];
            } else if (settings.monthDays?.length > 0) {
                options.bymonthday = settings.monthDays.map(d => parseInt(d));
            }
            break;

        case 'Yearly':
            if (settings.months?.length > 0) {
                options.bymonth = settings.months;
            }
            if (settings.monthlyType === 'weekday' && settings.weekPosition && settings.weekday) {
                const position = WEEK_POSITION_MAP[settings.weekPosition];
                const weekday = WEEKDAY_MAP[settings.weekday];
                options.byweekday = [weekday.nth(position)];
            } else if (settings.monthDays?.length > 0) {
                options.bymonthday = settings.monthDays.map(d => parseInt(d));
            }
            break;
    }

    return new RRule(options);
}

/**
 * Parse an RRule string back into user-friendly settings
 * @param {string} rruleString - RRule string (e.g., "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE,FR")
 * @returns {Object} - User-friendly settings object
 */
export function parseRRuleToSettings(rruleString) {
    if (!rruleString) return null;

    try {
        const rule = RRule.fromString(rruleString);
        const options = rule.origOptions;

        const settings = {
            frequency: Object.keys(FREQUENCY_MAP).find(k => FREQUENCY_MAP[k] === options.freq) || 'Weekly',
            interval: options.interval || 1,
            endDate: options.until || null,
            weekdays: [],
            monthDays: [],
            months: [],
            monthlyType: 'date',
            weekPosition: null,
            weekday: null
        };

        // Parse weekdays
        if (options.byweekday) {
            const weekdays = Array.isArray(options.byweekday) ? options.byweekday : [options.byweekday];
            
            weekdays.forEach(wd => {
                if (typeof wd === 'number') {
                    settings.weekdays.push(RRULE_WEEKDAY_LABELS[wd]);
                } else if (wd.n) {
                    // This is an nth weekday (e.g., 2nd Tuesday)
                    settings.monthlyType = 'weekday';
                    settings.weekPosition = Object.keys(WEEK_POSITION_MAP).find(k => WEEK_POSITION_MAP[k] === wd.n);
                    settings.weekday = RRULE_WEEKDAY_LABELS[wd.weekday];
                } else if (wd.weekday !== undefined) {
                    settings.weekdays.push(RRULE_WEEKDAY_LABELS[wd.weekday]);
                }
            });
        }

        // Parse month days
        if (options.bymonthday) {
            settings.monthDays = Array.isArray(options.bymonthday) ? options.bymonthday : [options.bymonthday];
            settings.monthlyType = 'date';
        }

        // Parse months
        if (options.bymonth) {
            settings.months = Array.isArray(options.bymonth) ? options.bymonth : [options.bymonth];
        }

        return settings;
    } catch (e) {
        console.error('Failed to parse RRule:', e);
        return null;
    }
}

/**
 * Get upcoming dates for a recurrence pattern
 * @param {string} rruleString - RRule string
 * @param {number} count - Number of dates to return
 * @param {Date} after - Start generating dates after this date (defaults to now)
 * @param {Date} before - Stop generating dates before this date (optional)
 * @returns {Date[]} - Array of upcoming dates (in local time)
 */
export function getUpcomingDates(rruleString, count = 5, after = null, before = null) {
    if (!rruleString) return [];

    try {
        const rule = RRule.fromString(rruleString);
        
        let dates;
        if (before) {
            const afterRRule = after ? localDateToRRuleDate(after) : localDateToRRuleDate(new Date());
            const beforeRRule = localDateToRRuleDate(before);
            dates = rule.between(afterRRule, beforeRRule, true).slice(0, count);
        } else {
            dates = rule.all((date, i) => i < count);
        }
        
        // Convert RRule UTC dates back to local dates
        return dates.map(rruleDateToLocalDate);
    } catch (e) {
        console.error('Failed to get upcoming dates:', e);
        return [];
    }
}

/**
 * Generate a human-readable description of the recurrence pattern
 * @param {string} rruleString - RRule string
 * @returns {string} - Human-readable description
 */
export function getRecurrenceDescription(rruleString) {
    if (!rruleString) return '';

    try {
        const rule = RRule.fromString(rruleString);
        return rule.toText();
    } catch (e) {
        console.error('Failed to get recurrence description:', e);
        return '';
    }
}

/**
 * Convert legacy recurrence fields to RRule string
 * Used for migrating existing events
 * @param {Object} event - Event with legacy recurrence fields
 * @returns {string|null} - RRule string or null if not recurring
 */
export function legacyToRRule(event) {
    if (!event.isRecurring || !event.recurringFrequency) return null;

    const startDate = new Date(event.defaultStartTime);
    
    const settings = {
        frequency: event.recurringFrequency === 'Days' ? 'Daily' :
                   event.recurringFrequency === 'Weeks' ? 'Weekly' :
                   event.recurringFrequency === 'Months' ? 'Monthly' :
                   event.recurringFrequency === 'Years' ? 'Yearly' : 'Weekly',
        interval: event.recurringInterval || 1,
        endDate: event.recurrenceEndDate ? new Date(event.recurrenceEndDate) : null,
        weekdays: event.recurrenceDaysOfWeek || [],
        monthDays: event.recurrenceDaysOfMonth?.map(d => parseInt(d)) || [],
        months: event.recurrenceMonths?.map(m => {
            const monthMap = { 'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6, 
                              'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12 };
            return monthMap[m] || 1;
        }) || [],
        monthlyType: event.recurrenceWeeksOfMonth?.length > 0 ? 'weekday' : 'date',
        weekPosition: event.recurrenceWeeksOfMonth?.[0] || null,
        weekday: event.recurrenceDaysOfWeek?.[0] || null
    };

    try {
        const rule = buildRRule(settings, startDate);
        return rule.toString();
    } catch (e) {
        console.error('Failed to convert legacy recurrence:', e);
        return null;
    }
}

/**
 * Validate recurrence settings
 * @param {Object} settings - Recurrence settings
 * @returns {{ valid: boolean, error: string|null }}
 */
export function validateRecurrenceSettings(settings) {
    if (!settings.frequency) {
        return { valid: false, error: 'Please select a frequency' };
    }

    if (!settings.interval || settings.interval < 1) {
        return { valid: false, error: 'Interval must be at least 1' };
    }

    if (settings.frequency === 'Weekly' && (!settings.weekdays || settings.weekdays.length === 0)) {
        return { valid: false, error: 'Please select at least one day of the week' };
    }

    if (settings.frequency === 'Monthly') {
        if (settings.monthlyType === 'weekday') {
            if (!settings.weekPosition || !settings.weekday) {
                return { valid: false, error: 'Please select which week and day' };
            }
        } else if (!settings.monthDays || settings.monthDays.length === 0) {
            return { valid: false, error: 'Please select at least one day of the month' };
        }
    }

    return { valid: true, error: null };
}

export { WEEKDAY_LABELS, WEEKDAY_SHORT, RRULE_WEEKDAY_LABELS, FREQUENCY_MAP, WEEK_POSITION_MAP };
